import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * ErrorBoundary — ostatnia siatka bezpieczeństwa dla crashy renderu React
 * (sesja 4.F.3). Owija `<App/>` w `main.tsx`.
 *
 * Bez tego niespodziewany wyjątek w render/lifecycle dowolnego komponentu
 * odmontowywał całe drzewo Reacta → biała strona. Teraz pokazujemy przyjazny
 * ekran z przyciskiem odświeżenia.
 *
 * ⚠ Error boundary łapie TYLKO błędy renderu/lifecycle. Błędy w handlerach
 * zdarzeń i kodzie async (fetch, runEconomyAction) NIE są łapane — te mają
 * własną obsługę (error state w modalach, catch w AutoSaveBridge, globalny
 * 401 handler w api.ts). To jest siatka na to, co przecieknie.
 *
 * React wymaga class component dla error boundary (brak hooka odpowiednika).
 */

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Log do konsoli — w produkcji można by wysłać do backendu (poza MVP).
    console.error('[ErrorBoundary] Render crash:', error, info.componentStack);
  }

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    // Pełnoekranowy fallback. z-[100] — nad modalami (z-40/50) i hatch (z-50).
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-meadow-200 font-game p-4 pointer-events-auto">
        <div className="bg-white rounded-3xl p-6 shadow-2xl max-w-sm w-full text-center">
          <h2 className="text-2xl font-bold text-meadow-700 mb-2">Ups! Coś się popsuło</h2>
          <p className="text-sm text-gray-600 mb-5">
            Gra napotkała niespodziewany błąd. Odśwież stronę — Twój postęp jest
            zapisany na serwerze.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="w-full bg-meadow-500 hover:bg-meadow-600 active:scale-95 transition rounded-2xl py-3 text-white font-bold text-lg shadow-md"
          >
            Odśwież grę
          </button>
        </div>
      </div>
    );
  }
}
